import { forwardRef, useContext, useEffect, useRef, useState } from 'react';

import { ApplicationContext } from '@src/app/contexts/Application';

import BackButton from '../../components/BackButton/BackButton';
import ProgressMenu from '../../components/Progress/ProgressMenu';

import IncomingView from './IncomingView';

export interface IncomingsViewProps {
    className?: string;
    incomingsData?: any;
    applicantsData?: any;
    activeApplicant?: number | null;
    setIncomingsData: Function;
    saveIncomings: (
        data: any,
        isPrimaryApplicant: boolean,
        noOfApplicants?: number,
    ) => Promise<any>;
}

const IncomingsView = forwardRef((props: IncomingsViewProps, ref) => {
    const [activeApplicant, setActiveApplicant] = useState(
        props.activeApplicant || 0,
    );
    const [applicantCount, setApplicantCount] = useState(1);
    const { applicants, setCurrentForm } = useContext(ApplicationContext);
    const firstViewRef = useRef(null);
    const secondViewRef = useRef(null);
    const [isFirst, setIsFirst] = useState(false);
    const [isSecond, setIsSecond] = useState(false);

    const topRef = useRef<HTMLDivElement>(null);

    const currentPath = window.location.pathname;

    useEffect(() => {
        if (props.activeApplicant) {
            setActiveApplicant(props.activeApplicant);
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [props.activeApplicant]);

    useEffect(() => {
        setApplicantCount(applicants.length);
    }, [applicants, setCurrentForm]);

    useEffect(() => {
        if (currentPath === '/eligibility/income') {
            setIsFirst(true);
            setIsSecond(false);
        } else {
            setIsFirst(false);
            setIsSecond(true);
        }
    }, [currentPath, activeApplicant]);

    return (
        <div className='flex justify-between flex-col sm:flex-row' ref={topRef}>
            <div className='w-full sm:w-1/2 lg:w-2/5 mt-20 sm:mt-0 sm:mr-10'>
                <div className='hidden lg:block mb-2'>
                    <BackButton />
                </div>
                <div className='text-2xl lg:text-4xl font-sora-bold tracking-tighter'>
                    {isFirst && <p>Your income</p>}
                    {isSecond && <p>{applicants[1].firstName}'s income</p>}
                </div>
                <div className='mt-3 border-l-4 border-periwinkle-500'>
                    <p className='ml-4 text-body'>
                        So we can help you understand how much you can borrow
                    </p>
                </div>
                <div className='relative mb-15'>
                    {isFirst && (
                        <IncomingView
                            formRef={(ref: any) => {
                                firstViewRef.current = ref;
                            }}
                            saveRef={ref}
                            setActiveApplicant={setActiveApplicant}
                            applicantCount={applicantCount}
                            setIncomeData={(data: any) =>
                                props.setIncomingsData(data, 0)
                            }
                            incomeData={props.incomingsData?.[0] || {}}
                            saveIncomings={props.saveIncomings}
                            isPrimaryApplicant={true}
                        />
                    )}
                    {applicantCount === 2 && isSecond && (
                        <IncomingView
                            formRef={(ref: any) => {
                                secondViewRef.current = ref;
                            }}
                            saveRef={ref}
                            setActiveApplicant={setActiveApplicant}
                            applicantCount={applicantCount}
                            setIncomeData={(data: any) =>
                                props.setIncomingsData(data, 1)
                            }
                            incomeData={props.incomingsData?.[1] || {}}
                            secondary
                            saveIncomings={props.saveIncomings}
                            isPrimaryApplicant={false}
                        />
                    )}
                </div>
            </div>
            <ProgressMenu />
        </div>
    );
});

export default IncomingsView;
